import {omit, pick, isPlainObject} from 'lodash';
import {weakMemo, singleMemo, once} from '@packages/utilities/memo';
import * as KeyMap from '@packages/utilities/key-map';
import {
  constantFalse,
  constant,
  emptyObject,
} from '@packages/utilities/fp/utils';
import {getElementTypeAndDataType} from '@packages/systems/core/render';
import {type NodePojo, isNodePojo} from '@packages/systems/core/nodepojo';
import {Json} from '@packages/utilities/json';
import {
  type Qualified,
  MutableQualifiedMap,
  getPluginName,
  getName,
  qualify,
  namesEqual,
  typeName,
  TypeName,
} from '@packages/systems/wfdl-Qualified';
import {
  type Type,
  withSystem,
  matchExpression,
  Component,
  matchType,
  Plugins,
  Types,
  Expressions,
  TypeImplementation,
  getSystem,
} from '@packages/systems/wfdl';
import {
  booleanTypeName,
  numberTypeName,
  textTypeName,
  listTypeName,
  TEmptyRecord,
  unionTypeName,
  enumTypeName,
  EElement,
  EText,
  ERecord,
  EBoolean,
  ENumber,
  EEnum,
  EUnion,
  EList,
  ELiteral,
  EHole,
  EDistinct,
  EFunction,
  type Expression,
  type ExpressionElement,
  TElement,
} from '@packages/systems/wfdl-ast';

type PojoData =
  | boolean
  | number
  | string
  | Array<PojoData>
  | {[key: string]: PojoData};

const isObject = (thing: PojoData): thing is {[key: string]: PojoData} =>
  isPlainObject(thing);

const richTextChildrenType = once(() =>
  qualify(Plugins.name('Basic'), typeName('RichTextChildren'))
);
const stringElementType = once(() =>
  qualify(Plugins.name('Basic'), Component.name('String'))
);

type ElementData = {[key: string]: any};
type SplitDataType =
  | ElementData
  | ExpressionElement
  | number
  | string
  | boolean
  | Json
  | undefined;

const splitLiteral = (
  val: ExpressionElement | number | string | boolean
): [ExpressionElement | number | string | boolean, undefined] => [
  val,
  undefined,
];

const splitDataBindExpressionMatcherForElement = matchExpression<
  [SplitDataType, Expression | undefined]
>({
  Record: (fields) => {
    const [newElementData, newFields] = KeyMap.keys(fields).reduce(
      (acc, key) => {
        const [data, bind] = acc;
        const fieldValue = fields[key];
        if (fieldValue == null) return acc;
        const [fieldElementData, fieldBind] =
          splitDataAndBindForElement(fieldValue);

        if (fieldElementData != null) {
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
          data[key] = fieldElementData;
        }

        if (fieldBind) {
          if (bind) {
            // @ts-expect-error - TS2322 - Type 'any' is not assignable to type 'never'.
            bind[key] = fieldBind;
          } else {
            // @ts-expect-error - TS2322 - Type '{ [x: string]: any; }' is not assignable to type 'undefined'.
            acc[1] = {[key]: fieldBind};
          }
        }

        return acc;
      },
      [{}, undefined]
    );
    return [newElementData, newFields ? ERecord(newFields) : undefined];
  },
  Union: ({tag, value}) => {
    const [elementData, bind] = splitDataAndBindForElement(value);
    return [
      elementData != null ? {tag, val: elementData} : undefined,
      bind ? EUnion({tag, value: bind}) : undefined,
    ];
  },
  Boolean: splitLiteral,
  Number: splitLiteral,
  Text: splitLiteral,
  Enum: splitLiteral,
  List: (val) => {
    const splitItems = val.map(splitDataAndBindForElement);
    const allAreStatic = splitItems.every(
      ([_, bind]: [any, any]) => bind === undefined
    );

    return allAreStatic
      ? [
          // @ts-expect-error - TS2345 - Argument of type '([elementData]: [any]) => any' is not assignable to parameter of type '(value: [ElementData | undefined, any], index: number, array: [ElementData | undefined, any][]) => any'.
          splitItems.map(([elementData]: [any]) => elementData as any),
          undefined,
        ]
      : [undefined, EList(val)];
  },
  Element: splitLiteral,
  Literal: (val) => [val.value, undefined],
  Distinct: (val) => {
    const [data, bind] = splitDataAndBindForElement(val.value);
    // If anything inside the Distinct has a binding, keep the entire
    // Distinct expression (with both static and dynamic parts) in bind.
    if (bind) {
      return [undefined, EDistinct({name: val.name, value: val.value})];
    }
    return [data, undefined];
  },
  _: (data) => [undefined, data],
});

const splitDataAndBindForElement = weakMemo(
  (
    element: Expression
  ): [SplitDataType | undefined, Expression | undefined] => {
    const [data, bind] = splitDataBindExpressionMatcherForElement(element);

    return [data, bind];
  }
);

/**
  NOTE: This should ONLY be used for Element->JSONNode conversion.
 */
export const getElementDataAndBindForJSONNode = singleMemo(
  (components: Component.ComponentMap) => {
    return weakMemo(
      (
        element: ExpressionElement
      ): {
        data: ElementData | undefined;
        bind: Expression | undefined;
      } => {
        const {type} = element;
        const dataExpr = Expressions.getElementData(element);
        const componentNameOnElement = getName(type);
        // Atoms are not in componentMap so if we find `type` there the
        // element is a component instance.  (plugin component or a user
        // component)
        const dataType = components.get(type)?.dataType;
        const isComponentInst = Boolean(dataType);

        const instOverrideFields = dataType
          ? Types.getRecordFields(dataType)
          : null;

        const [data, bind] = splitDataAndBindForElement(dataExpr);

        // Refine the type of data to just objects
        const usableData =
          typeof data === 'object' && !Array.isArray(data) && data !== null
            ? data
            : {};

        // Remove override field ids from "data" since those are later added
        // to sym.overrides instead
        const elementData = omit(
          usableData,
          KeyMap.keys(instOverrideFields || {})
        );
        // When working with a symbol instance, `bind` must take the shape to
        // match `sym.overrides`:
        const finalBind =
          isComponentInst && bind && Expressions.isRecord(bind)
            ? ERecord({
                sym: ERecord({
                  overrides: bind,
                }),
              })
            : bind;

        if (isComponentInst && getPluginName(type) !== '__SitePlugin') {
          // this is a plugin component instance
          return {
            data: {
              sym: {
                inst: componentNameOnElement,
              },
              ...usableData,
            },
            bind: finalBind,
          };
        }

        if (isComponentInst) {
          // @ts-expect-error - TS2339 - Property 'sym' does not exist on type 'Omit<ElementData, string>'.
          if (!elementData.sym) {
            // @ts-expect-error - TS2339 - Property 'sym' does not exist on type 'Omit<ElementData, string>'.
            elementData.sym = {};
          }

          // @ts-expect-error - TS2339 - Property 'sym' does not exist on type 'Omit<ElementData, string>'.
          elementData.sym.inst = componentNameOnElement;

          const elementOverrides =
            data && instOverrideFields
              ? pick(data, KeyMap.keys(instOverrideFields))
              : {};

          if (elementOverrides && Object.keys(elementOverrides).length > 0) {
            // @ts-expect-error - TS2339 - Property 'sym' does not exist on type 'Omit<ElementData, string>'.
            elementData.sym.overrides = elementOverrides;
          }
        }

        return {
          data: elementData,
          bind: finalBind,
        };
      }
    );
  }
);

const naiveDataToExpr = weakMemo((plugins: Plugins.PluginsType) =>
  weakMemo((dataTypes: Plugins.DataTypes) =>
    // @ts-expect-error this should also be void
    (data: any, childrenSet: WeakSet<NodePojo>): Expression => {
      if (typeof data === 'boolean') return EBoolean(data);
      if (typeof data === 'number') return ENumber(data);
      if (typeof data === 'string') return EText(data);

      if (isNodePojo(data) && childrenSet.has(data))
        return EElement(nodePojoToElement(plugins)(dataTypes)(data));

      if (Array.isArray(data))
        return EList(
          data.reduce<Array<Expression>>((items, item) => {
            // There are some codepaths that introduce null and undefined
            // values. Make sure we ignore them.
            if (item != null) {
              items.push(
                naiveDataToExpr(plugins)(dataTypes)(item, childrenSet)
              );
            }
            return items;
          }, [])
        );

      if (data && typeof data === 'object')
        return ERecord(
          Object.entries(data).reduce<Record<string, Expression>>(
            (acc, [key, val]) => {
              // There are some codepaths that introduce null and undefined
              // values. Makes sure we ignore them.
              if (val != null) {
                acc[key] = naiveDataToExpr(plugins)(dataTypes)(
                  val,
                  childrenSet
                );
              }
              return acc;
            },
            {}
          )
        );
    }
  )
);

const naiveExpr = constant(
  (
    data: PojoData,
    dataTypesById: Plugins.DataTypes,
    plugins: Plugins.PluginsType,
    childrenSet: WeakSet<NodePojo>
  ) => naiveDataToExpr(plugins)(dataTypesById)(data, childrenSet)
);

const elementExpr = constant(
  (
    data: PojoData,
    dataTypesById: Plugins.DataTypes,
    plugins: Plugins.PluginsType,
    childrenSet: WeakSet<NodePojo>
  ) =>
    isNodePojo(data)
      ? EElement(nodePojoToElement(plugins)(dataTypesById)(data))
      : naiveDataToExpr(plugins)(dataTypesById)(data, childrenSet)
);

const buildDataExprWithType = matchType<
  (
    data: PojoData,
    dataTypes: Plugins.DataTypes,
    plugins: Plugins.PluginsType,
    childrenSet: WeakSet<NodePojo>
  ) => Expression
>({
  TypeReference: naiveExpr,
  Element: elementExpr,
  TypeConstructor:
    (name, ty) => (data, dataTypesById, plugins, childrenSet) => {
      // Text type is a TypeConstructor but needs special handling to only create an EText
      if (namesEqual(name, textTypeName) && typeof data === 'string') {
        return EText(data);
      }

      // Boolean type is a TypeConstructor but needs special handling to only create an EBoolean
      if (namesEqual(name, booleanTypeName) && typeof data === 'boolean') {
        return EBoolean(data);
      }

      // Number type is a TypeConstructor but needs special handling to only create an ENumber
      if (namesEqual(name, numberTypeName) && typeof data === 'number') {
        return ENumber(data);
      }

      // RichTextChildren is an odd-ball; we still need to convert its insides.
      if (namesEqual(name, richTextChildrenType()))
        return naiveDataToExpr(plugins)(dataTypesById)(data, childrenSet);

      // Handle Custom and Unique types
      const typeImpl = Plugins.getDataTypes(plugins).get(name);

      // Unknown type
      if (!typeImpl) {
        return naiveDataToExpr(plugins)(dataTypesById)(data, childrenSet);
      }

      return TypeImplementation.match({
        Custom: () => {
          return ELiteral({name, value: data});
        },
        Unique: () => {
          const innerType = Types.getUniqueType(ty, plugins);
          if (!innerType) {
            throw new Error(
              `Unique type ${Types.print(ty)} has no inner type.`
            );
          }
          const distinctData = buildDataExpr(
            plugins,
            dataTypesById,
            innerType,
            data,
            childrenSet
          );
          return EDistinct({
            name,
            value: distinctData,
          });
        },
        Alias: () => naiveDataToExpr(plugins)(dataTypesById)(data, childrenSet),
      })(typeImpl);
    },
  TypeApplication:
    ({con}) =>
    (data, dataTypesById, plugins, childrenSet) => {
      const name = Types.getTypeConstructor(con);

      // Enum type is a TypeApplication but needs special handling to only create an EEnum
      if (name && namesEqual(name, enumTypeName) && typeof data === 'string')
        return EEnum(data);

      return naiveDataToExpr(plugins)(dataTypesById)(data, childrenSet);
    },
  Generic:
    ({body}) =>
    (data, dataTypesById, plugins, childrenSet) =>
      buildDataExpr(plugins, dataTypesById, body, data, childrenSet),
  _: (ty) => () => {
    throw new Error(
      `Data has an unknown type: ${Types.print(
        ty
      )} is not being handled properly`
    );
  },
});

const buildDataExpr = (
  plugins: Plugins.PluginsType,
  dataTypesById: Plugins.DataTypes,
  dataType: Type | undefined,
  data: PojoData,
  childrenSet: WeakSet<NodePojo>
): Expression => {
  if (!dataType)
    return naiveDataToExpr(plugins)(dataTypesById)(data, childrenSet);
  const aliasType = Types.getAliasType(dataType, plugins);
  if (aliasType) {
    return buildDataExpr(plugins, dataTypesById, aliasType, data, childrenSet);
  }
  const fields = Types.getRecordFields(dataType);
  if (fields) {
    if (!isObject(data))
      return naiveDataToExpr(plugins)(dataTypesById)(data, childrenSet);

    const properties = Object.entries(data).reduce<Record<string, Expression>>(
      (acc, [key, val]) => {
        // Some nodes contain null and undefined values even in
        // the presence of a dataType.
        if (val != null) {
          acc[key] = buildDataExpr(
            plugins,
            dataTypesById,
            fields[key],
            val,
            childrenSet
          );
        }
        return acc;
      },
      {}
    );

    return ERecord(properties);
  }

  const unionValues = Types.getUnionValues(dataType);
  if (unionValues) {
    if (!isObject(data))
      return naiveDataToExpr(plugins)(dataTypesById)(data, childrenSet);

    const {tag, val} = data;
    if (typeof tag !== 'string' || val == null)
      return naiveDataToExpr(plugins)(dataTypesById)(data, childrenSet);

    const valType = unionValues[tag];

    return EUnion({
      tag,
      value: buildDataExpr(plugins, dataTypesById, valType, val, childrenSet),
    });
  }

  const listType = Types.getListItemType(dataType);
  if (listType) {
    if (!Array.isArray(data)) {
      return naiveDataToExpr(plugins)(dataTypesById)(data, childrenSet);
    }
    const properties = data.reduce<Array<Expression>>((items, item) => {
      // There are some codepaths that introduce null and undefined
      // values. Make sure we ignore them.
      if (item != null) {
        items.push(
          buildDataExpr(plugins, dataTypesById, listType, item, childrenSet)
        );
      }
      return items;
    }, []);
    return EList(properties);
  }

  return buildDataExprWithType(dataType)(
    data,
    dataTypesById,
    plugins,
    childrenSet
  );
};

// Checks that a given dataType has a top level field called `children`, drills
// through given aliases.
const supportsChildren = (
  dataType: Type,
  plugins: Plugins.PluginsType
): boolean => {
  const aliasType = Types.getAliasType(dataType, plugins);
  if (aliasType) {
    return supportsChildren(aliasType, plugins);
  }

  const fields = Types.getRecordFields(dataType);

  return Boolean(fields && fields.children);
};

const matchLiteralOrDistinctName = matchExpression({
  Distinct:
    ({name}) =>
    (expectedName: Qualified<TypeName>) => {
      return namesEqual(name, expectedName);
    },
  Literal:
    ({name}) =>
    (expectedName: Qualified<TypeName>) => {
      return namesEqual(name, expectedName);
    },
  _: (_) => constantFalse,
});

const handleCustomAndUniqueTypes = (
  name: Qualified<TypeName>,
  plugins: Plugins.PluginsType,
  dataTypeDefaultValue: any,
  type: Type,
  expression: Expression | undefined
) => {
  const typeImpl = Plugins.getDataTypes(plugins).get(name);

  // Unknown type
  if (!typeImpl) {
    throw new Error('Unknown type');
  }

  return TypeImplementation.match({
    Custom: () => {
      return ELiteral({name, value: dataTypeDefaultValue});
    },
    Unique: () => {
      const innerType = Types.getUniqueType(type, plugins);
      if (!innerType) {
        throw new Error(`Unique type ${Types.print(type)} has no inner type.`);
      }
      // If the innerType does not have a distinct default value append in the
      // one we have at the Unique level
      const innerSys = getSystem(innerType);
      const innerTypeWithDefaultValue =
        innerSys && innerSys.defaultValue
          ? innerType
          : withSystem({defaultValue: dataTypeDefaultValue}, innerType);

      const distinctData = getCurrentOrDefaultExpression(plugins)(
        innerTypeWithDefaultValue,
        expression
      );
      return EDistinct({
        name,
        value: distinctData,
      });
    },
    // Before we call getExpressionWithType we unwrap Aliases
    Alias: () => {
      throw new Error('Unexpected Alias Type');
    },
  })(typeImpl);
};
const getExpressionWithType = matchType<
  (
    arg1: Expression | undefined,
    arg2: Plugins.PluginsType,
    arg3: Type,
    arg4?: any
  ) => Expression
>({
  Element: constant((expression, plugins, _, dataTypeDefaultValue) => {
    if (expression && Expressions.isElement(expression)) return expression;
    const dataTypes = Plugins.getPluginDataTypes(plugins);
    return EElement(
      nodePojoToElement(plugins)(dataTypes)(dataTypeDefaultValue)
    );
  }),
  TypeApplication:
    ({con}, ty) =>
    (
      expression: undefined | Expression,
      plugins: Plugins.PluginsType,
      dataType: Type,
      dataTypeDefaultValue: any
    ) => {
      const deconstructedTypeApplication =
        Types.deconstructTypeApplication(con);
      if (!deconstructedTypeApplication) return EHole();
      const name = deconstructedTypeApplication.con;

      // If expression is a literal or unique and the name and TypeApplication name match, return the expression
      if (expression && matchLiteralOrDistinctName(expression)(name))
        return expression;

      // Enum type is a TypeApplication but needs special handling to only create an EEnum
      if (namesEqual(enumTypeName, name)) {
        if (expression && Expressions.isEnum(expression)) return expression;
        return EEnum(dataTypeDefaultValue);
      }

      // Union type is a TypeApplication but needs special handling to only create an EUnion
      if (namesEqual(unionTypeName, name)) {
        return expression
          ? getCurrentOrDefaultUnion(expression)(
              plugins,
              dataType,
              dataTypeDefaultValue
            )
          : defaultUnion(plugins, dataType, dataTypeDefaultValue);
      }

      // List type is a TypeApplication but needs special handling to only create an EList
      if (namesEqual(listTypeName, name)) {
        return expression
          ? getCurrentOrDefaultList(expression)(
              plugins,
              dataType,
              dataTypeDefaultValue
            )
          : defaultList(plugins, dataType, dataTypeDefaultValue);
      }

      return handleCustomAndUniqueTypes(
        name,
        plugins,
        dataTypeDefaultValue,
        ty,
        expression
      );
    },
  TypeConstructor:
    (name, ty) =>
    (
      expression: undefined | Expression,
      plugins: Plugins.PluginsType,
      dataType: Type,
      dataTypeDefaultValue: any
    ) => {
      if (
        expression &&
        shouldReturnExpressionForTypeConstructor(expression)(name)
      ) {
        return expression;
      }

      // Text type is a TypeConstructor but needs special handling to only create an EText
      if (namesEqual(textTypeName, name)) {
        return EText(dataTypeDefaultValue);
      }

      // Boolean type is a TypeConstructor but needs special handling to only create an EBoolean
      if (namesEqual(booleanTypeName, name)) {
        return EBoolean(dataTypeDefaultValue);
      }

      // Number type is a TypeConstructor but needs special handling to only create an ENumber
      if (namesEqual(numberTypeName, name)) {
        return ENumber(dataTypeDefaultValue);
      }

      if (namesEqual(richTextChildrenType(), name)) {
        return EList(
          // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
          dataTypeDefaultValue.map((item) =>
            getCurrentOrDefaultExpression(plugins)(
              withSystem({defaultValue: item}, TElement)
            )
          )
        );
      }

      return handleCustomAndUniqueTypes(
        name,
        plugins,
        dataTypeDefaultValue,
        ty,
        expression
      );
    },
  Generic:
    ({body}) =>
    (expression: undefined | Expression, plugins: Plugins.PluginsType) =>
      getCurrentOrDefaultExpression(plugins)(body, expression),
  _: constant(() => EHole()),
});

export const getCurrentOrDefaultExpression = weakMemo(
  (plugins: Plugins.PluginsType) =>
    (originalDataType: Type, expression?: Expression): Expression => {
      if (expression && Expressions.isBinding(expression)) return expression;
      if (!originalDataType && expression) return expression;
      const dataType = Types.unwrapAliasType(originalDataType, plugins);

      const dataTypeDefaultValue =
        Types.getTypeDefaultValue(plugins)(originalDataType);

      const fields = Types.getRecordFields(dataType);

      if (fields) {
        return expression
          ? getCurrentOrDefaultRecord(expression)(
              plugins,
              fields,
              dataTypeDefaultValue
            )
          : defaultRecord(plugins, fields, dataTypeDefaultValue);
      }

      return getExpressionWithType(dataType)(
        expression,
        plugins,
        dataType,
        dataTypeDefaultValue
      );
    }
);

export function getFieldDefaultExpression(
  component: Component.ComponentType,
  fieldId: string,
  fieldType: Type,
  plugins: Plugins.PluginsType
): Expression {
  const defaultData = Component.getDefaultData(component);
  if (defaultData) {
    const fieldExpr = Expressions.getIn(defaultData, [
      {in: 'Function'},
      {in: 'Record', at: fieldId},
    ]);
    if (fieldExpr) {
      return fieldExpr;
    }
  }
  return getCurrentOrDefaultExpression(plugins)(fieldType);
}

/**
 * Walks the property expressions of a component's `defaultData` Record body
 * and lets the caller rewrite individual ones, then derives
 * `system.defaultValue` from whatever the caller returned.
 *
 * Returns the component unchanged if it has no `defaultData` or if
 * `defaultData`'s Function body is not a Record expression.
 */
export function updateDefaultDataPropertiesOnComponent(
  component: Component.ComponentType,
  updateProperty: (valueExpr: Expression) => Expression,
  plugins: Plugins.PluginsType
): Component.ComponentType {
  const currentDefaultData = Component.getDefaultData(component);
  if (!currentDefaultData) return component;

  const fn = Expressions.getFunction(currentDefaultData);
  if (!fn) return component;

  const currentProperties = Expressions.getRecord(fn.body);
  if (!currentProperties) return component;

  const nextProperties = KeyMap.map(updateProperty)(currentProperties);
  if (nextProperties === currentProperties) return component;

  const nextDefaultData = EFunction({
    param: fn.param,
    body: ERecord(nextProperties),
  });

  const dataType = Component.getDataType(component);
  const evalEnv = Plugins.getEvalEnv(plugins);
  let nextDataType = dataType;
  for (const [fieldKey, valueExpr] of Object.entries(nextProperties)) {
    if (valueExpr === currentProperties[fieldKey]) continue;
    const nextDefaultValue = Expressions.evaluate(evalEnv, valueExpr);
    nextDataType = Types.updateRecordField(nextDataType, fieldKey, (field) =>
      withSystem(
        {...(getSystem(field) ?? {}), defaultValue: nextDefaultValue},
        field
      )
    );
  }

  return Component.update(component, {
    dataType: nextDataType,
    defaultData: nextDefaultData,
  });
}

/********* matchExpression helper functions ***********/

const defaultRecord = (
  plugins: Plugins.PluginsType,
  fields: {[key: string]: Type},
  dataTypeDefaultValue: any
) =>
  ERecord(
    Object.keys(fields).reduce<Record<string, any>>((memo, key) => {
      const fieldType = dataTypeDefaultValue.hasOwnProperty(key)
        ? // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
          withSystem({defaultValue: dataTypeDefaultValue[key]}, fields[key])
        : fields[key];
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      memo[key] = getCurrentOrDefaultExpression(plugins)(fieldType);
      return memo;
    }, {})
  );

const getCurrentOrDefaultRecord = matchExpression({
  Record:
    (val) =>
    (
      plugins: Plugins.PluginsType,
      fields: {[key: string]: Type},
      dataTypeDefaultValue: any
    ) => {
      const properties = Object.keys(val).reduce<Record<string, any>>(
        (memo, key) => {
          memo[key] = getCurrentOrDefaultExpression(plugins)(
            // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
            fields[key],
            val[key]
          );
          return memo;
        },
        {}
      );
      // Add fields that might be missing from the expression passed in
      // @ts-expect-error - TS2345 - Argument of type '(memo: Record<string, any>, key: string) => void' is not assignable to parameter of type '(previousValue: Record<string, any>, currentValue: string, currentIndex: number, array: string[]) => Record<string, any>'.
      Object.keys(fields).reduce<Record<string, any>>((memo, key) => {
        if (!properties[key]) {
          const fieldType = dataTypeDefaultValue.hasOwnProperty(key)
            ? // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
              withSystem({defaultValue: dataTypeDefaultValue[key]}, fields[key])
            : fields[key];
          // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
          properties[key] = getCurrentOrDefaultExpression(plugins)(fieldType);
        }
      }, {});
      return ERecord(properties);
    },
  _: constant(defaultRecord),
});

const defaultUnion = (
  plugins: Plugins.PluginsType,
  dataType: Type,
  dataTypeDefaultValue: unknown
) => {
  const unionValues = Types.getUnionValues(dataType);
  if (!unionValues) {
    throw new Error('Expected union type');
  }

  const unionTagFallback = Object.keys(unionValues)[0];
  if (unionTagFallback === undefined) {
    throw new Error('Expected union type with at least one value');
  }

  const branchTypeFallback = unionValues[unionTagFallback];
  if (branchTypeFallback === undefined) {
    throw new Error('Expected union type with at least one value');
  }

  if (
    dataTypeDefaultValue != null &&
    !Array.isArray(dataTypeDefaultValue) &&
    typeof dataTypeDefaultValue === 'object' &&
    'tag' in dataTypeDefaultValue &&
    typeof dataTypeDefaultValue.tag === 'string'
  ) {
    const branchType = unionValues[dataTypeDefaultValue.tag];
    if (branchType) {
      return EUnion({
        tag: dataTypeDefaultValue.tag,
        value: getCurrentOrDefaultExpression(plugins)(
          'val' in dataTypeDefaultValue
            ? withSystem({defaultValue: dataTypeDefaultValue.val}, branchType)
            : branchType
        ),
      });
    }
  }

  return EUnion({
    tag: unionTagFallback,
    value: getCurrentOrDefaultExpression(plugins)(branchTypeFallback),
  });
};

const getCurrentOrDefaultUnion = matchExpression({
  Union: (val) => (plugins: Plugins.PluginsType, dataType: Type, _: any) =>
    EUnion({
      tag: val.tag,
      value: getCurrentOrDefaultExpression(plugins)(
        // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
        (Types.getUnionValues(dataType) || {})[val.tag],
        val.value
      ),
    }),
  _: constant(defaultUnion),
});

const defaultList = (
  plugins: Plugins.PluginsType,
  dataType: Type,
  dataTypeDefaultValue: unknown
) => {
  const listType = Types.getListItemType(dataType);
  if (
    !listType ||
    !dataTypeDefaultValue ||
    !Array.isArray(dataTypeDefaultValue)
  ) {
    return EList([]);
  }
  return EList(
    dataTypeDefaultValue.map((item: unknown) =>
      getCurrentOrDefaultExpression(plugins)(
        withSystem({defaultValue: item}, listType)
      )
    )
  );
};

const getCurrentOrDefaultList = matchExpression({
  List: (val) => (plugins: Plugins.PluginsType, dataType: Type, _: any) => {
    const listType = Types.getListItemType(dataType);
    if (!listType) return EList([]);
    return EList(
      val.map((item) => getCurrentOrDefaultExpression(plugins)(listType, item))
    );
  },
  _: constant(defaultList),
});

const shouldReturnExpressionForTypeConstructor = matchExpression({
  Literal: (val) => (typeConstructorName: Qualified<TypeName>) =>
    namesEqual(val.name, typeConstructorName),
  Distinct: (val) => (typeConstructorName: Qualified<TypeName>) =>
    namesEqual(val.name, typeConstructorName),
  Text: constant((typeConstructorName: Qualified<TypeName>) =>
    namesEqual(textTypeName, typeConstructorName)
  ),
  Boolean: constant((typeConstructorName: Qualified<TypeName>) =>
    namesEqual(booleanTypeName, typeConstructorName)
  ),
  Number: constant((typeConstructorName: Qualified<TypeName>) =>
    namesEqual(numberTypeName, typeConstructorName)
  ),
  List: constant((typeConstructorName: Qualified<TypeName>) =>
    namesEqual(richTextChildrenType(), typeConstructorName)
  ),
  _: (_) => constantFalse,
});

const getDataTypes = weakMemo((plugins: Plugins.PluginsType) => {
  const pluginDataTypes = Plugins.getPluginDataTypes(plugins);
  return weakMemo((components: Component.ComponentMap) =>
    components.size() === 0
      ? pluginDataTypes
      : components
          .reduce(accumulateRootDataType, pluginDataTypes.asMutableCopy())
          .asNonMutableCopy()
  );
});

const accumulateRootDataType = (
  acc: MutableQualifiedMap<Component.ComponentName, Type>,
  component: Component.ComponentType,
  componentName: Qualified<Component.ComponentName>
) => {
  acc.set_mut(componentName, Component.getDataType(component));
  return acc;
};

/**
 * Converts an `NodePojo` to `Element` for use with Richtext consumption.
 */
export const nodePojoToElementWithComponents = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((components: Component.ComponentMap) =>
      weakMemo((pojo: NodePojo): ExpressionElement => {
        const dataTypesById = getDataTypes(plugins)(components);

        return nodePojoToElement(plugins)(dataTypesById)(pojo);
      })
    )
);

const noSym = KeyMap.dissoc('sym');

export const nodePojoToElement = weakMemo((plugins: Plugins.PluginsType) =>
  weakMemo((dataTypesById: Plugins.DataTypes) =>
    weakMemo((pojo: NodePojo): ExpressionElement => {
      const {id, type, text, data = emptyObject} = pojo;
      const children = Array.isArray(pojo.children) ? pojo.children : [];

      if (text || !type) {
        const value = Object.hasOwn(data, 'value') ? data.value : '';
        return {
          id,
          type: stringElementType(),
          data: EText(value),
        };
      }

      const sym = data.sym;
      const instId = sym && sym.inst;

      const [elementType, maybeDataType] = getElementTypeAndDataType(
        plugins,
        dataTypesById,
        type,
        instId
      );

      if (instId && !maybeDataType) {
        throw new TypeError(
          `No dataType could be determined for component "${instId}" for instance with ID "${id}"`
        );
      }

      const dataType = maybeDataType || TEmptyRecord;
      const noSymData = noSym<PojoData>(data);
      const fullData = instId
        ? sym.overrides
          ? KeyMap.union<PojoData>(sym.overrides)(noSymData)
          : noSymData
        : children.length || supportsChildren(dataType, plugins)
          ? {
              ...noSymData,
              // NodePojo can have `null` type
              children: children as PojoData,
            }
          : noSymData;

      const childrenSet = new WeakSet<NodePojo>();
      for (const child of children ?? []) {
        if (child != null) childrenSet.add(child);
      }

      return {
        id,
        type: elementType,
        data: buildDataExpr(
          plugins,
          dataTypesById,
          dataType,
          fullData,
          childrenSet
        ),
      };
    })
  )
);

export function nodePojosToElements(
  nodePojos: NodePojo[],
  plugins: Plugins.PluginsType
): Expression {
  const dataTypes = Plugins.getPluginDataTypes(plugins);

  return EList(
    nodePojos.map((pojo) =>
      EElement(nodePojoToElement(plugins)(dataTypes)(pojo))
    )
  );
}
